import { useTooltip } from "context/TooltipProvider";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  fetchCrimeCategoryBaseData,
  fetchCrimeType,
  fetchProsecutorDecision,
} from "services/basedataServices";
import {
  addCountDefendantList,
  editCountDefendantList,
} from "services/caseServices";
import {
  AlternativeCrimeLevel,
  CaseCreation,
  CountDefendantsList,
  CrimeCategoryBaseData,
  CrimeLevel,
  CrimeTypeBaseData,
  CrimeTypeForCountAndAppeal,
  ProsecutorDecisionBaseData,
} from "types";
import { LoadingIcon } from "utils/constant";
import { errorHandlerOnForm } from "utils/helper";

type Props = {
  caseData: CaseCreation;
  action: "add" | "edit";
  selectedData?: CountDefendantsList;
  onClose: (refetch: boolean) => void;
};

const CountDefendantListForm: FC<Props> = ({
  caseData,
  action,
  selectedData,
  onClose,
}) => {
  const { showTooltip } = useTooltip();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [formState, setFormState] = useState<CountDefendantsList>();
  const [defendants, setDefendants] = useState<string[]>();

  useEffect(() => {
    if (selectedData) {
      const defendantIds = selectedData?.defendants_info
        ?.map((d) => d?.id)
        .filter((id): id is number => id !== undefined);
      setFormState({
        ...selectedData,
        defendants: defendantIds,
      });
      setDefendants(defendantIds?.map(String));
    }
  }, [selectedData]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormState(
      (prevState) =>
        ({
          ...(prevState ?? {}),
          [name]: value,
        }) as CountDefendantsList,
    );
  };

  const [baseData, setBaseData] = useState<{
    crimeType: CrimeTypeBaseData[];
    prosecutorDecision: ProsecutorDecisionBaseData[];
    crimeCategory: CrimeCategoryBaseData[];
  }>({
    crimeType: [],
    prosecutorDecision: [],
    crimeCategory: [],
  });

  useEffect(() => {
    fetchCrimeCategoryBaseData().then((data) =>
      setBaseData((prev) => ({
        ...prev,
        crimeCategory: data,
      })),
    );
    fetchCrimeType().then((data) =>
      setBaseData((prev) => ({ ...prev, crimeType: data })),
    );
    fetchProsecutorDecision().then((data) =>
      setBaseData((prev) => ({ ...prev, prosecutorDecision: data })),
    );
  }, []);

  const validateForm = (tab = "all") => {
    if (!defendants || defendants.length === 0) {
      return t("count_defendant_select_at_least_one");
    }
    if (!formState?.count_number) {
      return t("count_number_required");
    }
    if (!formState?.crime_type) {
      return t("crime_type_required");
    }
    if (!formState?.crime_type_category) {
      return t("crime_category_required");
    }
    if (!formState?.crime_level) {
      return t("crime_level_required");
    }
    if (!formState?.articles_ofthe_charge) {
      return t("articles_of_charge_required");
    }
    if (!formState?.prosecutor_decision) {
      return t("prosecutor_decision_required");
    }
    if (!formState?.prosecutor_decision_description) {
      return t("prosecutor_decision_description_required");
    }
    // Alternate crime validation if has_alternate is true
    if (formState?.has_alternate) {
      if (!formState?.alternate_crime_type) {
        return t("alternate_crime_type_required");
      }
      if (!formState?.alternate_crime_type_category) {
        return t("alternate_crime_category_required");
      }
      if (!formState?.alternate_crime_level) {
        return t("alternate_crime_level_required");
      }
      if (!formState?.alternate_articles_ofthe_charge) {
        return t("alternate_articles_of_charge_required");
      }
      if (!formState?.alternate_prosecutor_decision) {
        return t("alternate_prosecutor_decision_required");
      }
      if (!formState?.alternate_prosecutor_decision_description) {
        return t("alternate_prosecutor_decision_description_required");
      }
    }
    return "";
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    const error = validateForm();
    if (error) {
      showTooltip(error, "error");
      return;
    }

    setLoading(true);
    const data = {
      ...formState,
      case: caseData.id,
      defendants,
    };

    const api =
      action === "add" ? addCountDefendantList : editCountDefendantList;
    api(data)
      .then(() => {
        showTooltip(t("successful"), "success");
        onClose(true);
      })
      .catch((error) => {
        showTooltip(
          error?.response?.data?.detail || errorHandlerOnForm(error),
          "error",
        );
      })
      .finally(() => setLoading(false));
  };
  const handleClickOutside = (e: any) => {
    if (e.target.classList.contains("fixed")) {
      onClose(false);
    }
  };

  return (
    <div
      style={{ marginTop: "0px" }}
      className="fixed inset-0 z-50 flex justify-end bg-black bg-opacity-50 backdrop-blur-sm p-4"
      onClick={handleClickOutside}
    >
      <div className="relative w-full max-w-2xl bg-white p-8 shadow-lg rounded-lg overflow-y-auto">
        <h2 className="mb-1 text-2xl font-semibold">
          {t("count_defendant_list")}
        </h2>
        <p className="mb-6 text-xs font-light text-[#9D9D9D]">
          {t("publishing_industries_placeholder")}
        </p>

        <form className="space-y-4" onSubmit={handleSubmit}>
          <div>
            <div className="flex flex-col mt-4">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                {t("select_defendants")}
              </label>
              <select
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300 h-[100px]"
                value={defendants}
                onChange={(e) => {
                  setDefendants(
                    Array.from(
                      e.target.selectedOptions,
                      (option) => option.value,
                    ),
                  );
                }}
                multiple
              >
                {caseData?.case_defendant_links &&
                  caseData?.case_defendant_links.map((caseLink: any) => (
                    <option key={caseLink?.id} value={caseLink?.id}>
                      {t("defendant")} {caseLink?.defendant_number} :{" "}
                      {caseLink?.defendant_info?.first_name}{" "}
                      {caseLink?.defendant_info?.middle_name}{" "}
                      {caseLink?.defendant_info?.last_name}
                    </option>
                  ))}
              </select>
            </div>

            <div className="flex flex-col mt-4">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                {t("count_number")}
              </label>
              <input
                type="number"
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="count_number"
                value={formState?.count_number ?? ""}
                onChange={handleChange}
              />
            </div>
            <div className="flex flex-col mt-4">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                {t("has_alternate")}
              </label>
              <select
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="has_alternate"
                value={formState?.has_alternate ? "true" : "false"}
                onChange={(e) => {
                  setFormState((prev) => ({
                    ...prev,
                    has_alternate: e.target.value === "true",
                  }));
                }}
              >
                <option value="false">{t("no")}</option>
                <option value="true">{t("yes")}</option>
              </select>
            </div>
            {/* 
            <div className="flex flex-col mt-4">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                {t("crime_type")}
              </label>
              <input
                type="text"
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="crime_type"
                value={formState?.crime_type ?? ""}
                onChange={handleChange}
              />
            </div>
            <div className="flex flex-col mt-4">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Crime Type Category
              </label>
              <input
                type="text"
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="crime_type_category"
                value={formState?.crime_type_category ?? ""}
                onChange={handleChange}
              />
            </div> */}
            <div className="flex flex-col mt-4">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Crime Type
              </label>
              <select
                className="border capitalize border-gray-300 rounded-lg bg-[#F2F2F2] p-2 pr-8 focus:outline-none focus:ring focus:ring-blue-300"
                name="crime_type"
                value={formState?.crime_type ?? ""}
                onChange={handleChange}
              >
                <option value={""}>{t("select_crime_type")}</option>
                {Object.entries(CrimeTypeForCountAndAppeal).map(
                  ([key, value]) => (
                    <option key={key} value={key}>
                      {value}
                    </option>
                  ),
                )}
                {/* {baseData?.crimeType.map((type, index) => (
                  <option key={index} value={type?.name} className="capitalize">
                    {type?.name}
                  </option>
                ))} */}
              </select>
            </div>
            <div className="flex flex-col mt-4">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                {t("crime_category")}
              </label>
              <select
                className="border border-gray-300 rounded-lg bg-[#F2F2F2] p-2 pr-8 focus:outline-none focus:ring focus:ring-blue-300"
                name="crime_type_category"
                value={formState?.crime_type_category ?? ""}
                onChange={handleChange}
              >
                <option value={""}>{t("select_crime_category")}</option>
                {baseData?.crimeCategory
                  // ?.filter(
                  //   (cc) =>
                  //     cc?.crime_type ===
                  //     baseData?.crimeType?.find(
                  //       (ct) => ct?.name === formState?.crime_type
                  //     )?.id
                  // )
                  .map((type, index) => (
                    <option key={index} value={type?.name}>
                      {type?.name}
                    </option>
                  ))}
              </select>
            </div>

            <div className="flex flex-col mt-4">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                {t("crime_level")}
              </label>
              <select
                className="border border-gray-300 rounded-lg bg-[#F2F2F2] p-2 pr-8 focus:outline-none focus:ring focus:ring-blue-300"
                name="crime_level"
                value={formState?.crime_level ?? ""}
                onChange={handleChange}
              >
                <option value="">{t("select_crime_level")}</option>
                {Object.entries(CrimeLevel).map(([key, value]) => (
                  <option key={key} value={key}>
                    {value}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col mt-4">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                {t("articles_of_charge")}
              </label>
              <input
                type="text"
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="articles_ofthe_charge"
                value={formState?.articles_ofthe_charge ?? ""}
                onChange={handleChange}
              />
            </div>

            {/* <div className="flex flex-col mt-4">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                {t("prosecutor_decision")}
              </label>
              <input
                type="text"
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="prosecutor_decision"
                value={formState?.prosecutor_decision ?? ""}
                onChange={handleChange}
              />
            </div> */}
            <div className="flex flex-col mt-4">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Prosecutor Decision
              </label>
              <select
                className="border border-gray-300 rounded-lg bg-[#F2F2F2] p-2 pr-8 focus:outline-none focus:ring focus:ring-blue-300"
                name="prosecutor_decision"
                value={formState?.prosecutor_decision ?? ""}
                onChange={handleChange}
              >
                <option value={""}>{t("select_prosecutor_decision")}</option>
                {baseData?.prosecutorDecision.map((type, index) => (
                  <option key={index} value={type?.decision}>
                    {type?.decision}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col mt-4">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                {t("prosecutor_decision_description")}
              </label>
              <textarea
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="prosecutor_decision_description"
                value={formState?.prosecutor_decision_description ?? ""}
                onChange={handleChange}
                rows={4}
              />
            </div>

            {/* Alternate Crime Section */}
            {formState?.has_alternate && (
              <div className="mt-6 border-t pt-6">
                <h3 className="text-base font-semibold text-[#AA232F] mb-4">
                  {t("alternate_crime")}
                </h3>
                {/* <div className="flex flex-col mt-4">
                  <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                    {t("alternate_crime_type")}
                  </label>
                  <input
                    type="text"
                    className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                    name="alternate_crime_type"
                    value={formState?.alternate_crime_type ?? ""}
                    onChange={handleChange}
                  />
                </div> */}
                {/* <div className="flex flex-col mt-4">
                  <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                    Alternate Crime Type Category
                  </label>
                  <input
                    type="text"
                    className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                    name="alternate_crime_type_category"
                    value={formState?.alternate_crime_type_category ?? ""}
                    onChange={handleChange}
                  />
                </div> */}
                <div className="flex flex-col mt-4">
                  <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                    Alternate Crime Type
                  </label>
                  <select
                    className="border capitalize border-gray-300 rounded-lg bg-[#F2F2F2] p-2 pr-8 focus:outline-none focus:ring focus:ring-blue-300"
                    name="alternate_crime_type"
                    value={formState?.alternate_crime_type ?? ""}
                    onChange={handleChange}
                  >
                    <option value={""}>{t("select_crime_type")}</option>
                    {Object.entries(CrimeTypeForCountAndAppeal).map(
                      ([key, value]) => (
                        <option key={key} value={key}>
                          {value}
                        </option>
                      ),
                    )}
                    {/* {baseData?.crimeType.map((type, index) => (
                      <option
                        key={index}
                        value={type?.name}
                        className="capitalize"
                      >
                        {type?.name}
                      </option>
                    ))} */}
                  </select>
                </div>
                <div className="flex flex-col mt-4">
                  <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                    {t("alternate_crime_type_category")}
                  </label>
                  <select
                    className="border border-gray-300 rounded-lg bg-[#F2F2F2] p-2 pr-8 focus:outline-none focus:ring focus:ring-blue-300"
                    name="alternate_crime_type_category"
                    value={formState?.alternate_crime_type_category ?? ""}
                    onChange={handleChange}
                  >
                    <option value={""}>{t("select_crime_category")}</option>
                    {baseData?.crimeCategory
                      // ?.filter(
                      //   (cc) =>
                      //     cc?.crime_type ===
                      //     baseData?.crimeType?.find(
                      //       (ct) => ct?.name === formState?.crime_type
                      //     )?.id
                      // )
                      .map((type, index) => (
                        <option key={index} value={type?.name}>
                          {type?.name}
                        </option>
                      ))}
                  </select>
                </div>

                <div className="flex flex-col mt-4">
                  <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                    {t("alternate_crime_level")}
                  </label>
                  <select
                    className="border border-gray-300 rounded-lg bg-[#F2F2F2] p-2 pr-8 focus:outline-none focus:ring focus:ring-blue-300"
                    name="alternate_crime_level"
                    value={formState?.alternate_crime_level ?? ""}
                    onChange={handleChange}
                  >
                    <option value="">
                      {t("select_alternate_crime_level")}
                    </option>
                    {Object.entries(AlternativeCrimeLevel).map(
                      ([key, value]) => (
                        <option key={key} value={key}>
                          {value}
                        </option>
                      ),
                    )}
                  </select>
                </div>
                <div className="flex flex-col mt-4">
                  <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                    {t("alternate_articles_of_charge")}
                  </label>
                  <input
                    type="text"
                    className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                    name="alternate_articles_ofthe_charge"
                    value={formState?.alternate_articles_ofthe_charge ?? ""}
                    onChange={handleChange}
                  />
                </div>
                {/* <div className="flex flex-col mt-4">
                  <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                    {t("alternate_prosecutor_decision")}
                  </label>
                  <input
                    type="text"
                    className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                    name="alternate_prosecutor_decision"
                    value={formState?.alternate_prosecutor_decision ?? ""}
                    onChange={handleChange}
                  />
                </div> */}
                <div className="flex flex-col mt-4">
                  <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                    Alternate Prosecutor Decision
                  </label>
                  <select
                    className="border border-gray-300 rounded-lg bg-[#F2F2F2] p-2 pr-8 focus:outline-none focus:ring focus:ring-blue-300"
                    name="alternate_prosecutor_decision"
                    value={formState?.alternate_prosecutor_decision ?? ""}
                    onChange={handleChange}
                  >
                    <option value={""}>
                      {t("select_prosecutor_decision")}
                    </option>
                    {baseData?.prosecutorDecision.map((type, index) => (
                      <option key={index} value={type?.decision}>
                        {type?.decision}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-col mt-4">
                  <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                    {t("alternate_prosecutor_decision_description")}
                  </label>
                  <textarea
                    className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                    name="alternate_prosecutor_decision_description"
                    value={
                      formState?.alternate_prosecutor_decision_description ?? ""
                    }
                    onChange={handleChange}
                    rows={4}
                  />
                </div>
              </div>
            )}
          </div>
          <div className="flex justify-end mt-4">
            <button
              type="submit"
              className={`border border-[#212121] bg-[#212121] px-[76px] py-2 
                      text-white font-[Poppins] cursor-pointer font-[500] text-base rounded-md hover:bg-[#414040]
                              ${
                                loading ? "opacity-50 cursor-not-allowed" : ""
                              }`}
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  {LoadingIcon}
                  {t("loading")}
                </div>
              ) : action === "add" ? (
                t("add")
              ) : (
                t("edit")
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CountDefendantListForm;
